import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Check, Monitor, Moon, Palette, Settings, Sun, X } from 'lucide-react';
import SubscriptionExpirySetting from './SubscriptionExpirySetting';

const THEME_KEY = 'adminThemeMode';
const COLOR_KEY = 'adminPrimaryColor';

const MODES = [
    { id: 'light', label: 'Light', Icon: Sun },
    { id: 'dark', label: 'Dark', Icon: Moon },
    { id: 'system', label: 'System', Icon: Monitor },
];

const COLORS = [
    { id: 'matrimony', label: 'Matrimony', value: '#B71C1C' },
    { id: 'blue', label: 'Blue', value: '#2563EB' },
    { id: 'violet', label: 'Violet', value: '#7C3AED' },
    { id: 'emerald', label: 'Emerald', value: '#059669' },
    { id: 'rose', label: 'Rose', value: '#E11D48' },
    { id: 'orange', label: 'Orange', value: '#EA580C' },
];

const getSavedMode = () => {
    const value = localStorage.getItem(THEME_KEY);
    return MODES.some(mode => mode.id === value) ? value : 'system';
};

const getSavedColor = () => {
    const value = localStorage.getItem(COLOR_KEY);
    return COLORS.some(color => color.id === value) ? value : 'matrimony';
};

const ThemeSettings = ({ withSubscription = false }) => {
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState(getSavedMode);
    const [color, setColor] = useState(getSavedColor);
    const panelRef = useRef(null);

    useLayoutEffect(() => {
        const media = window.matchMedia('(prefers-color-scheme: dark)');
        const applyTheme = () => {
            const resolved = mode === 'system' ? (media.matches ? 'dark' : 'light') : mode;
            document.documentElement.dataset.adminTheme = resolved;
            document.documentElement.dataset.adminThemeMode = mode;
            document.documentElement.dataset.adminColor = color;
        };

        applyTheme();
        localStorage.setItem(THEME_KEY, mode);
        localStorage.setItem(COLOR_KEY, color);
        media.addEventListener?.('change', applyTheme);
        return () => media.removeEventListener?.('change', applyTheme);
    }, [mode, color]);

    useEffect(() => {
        if (!open) return undefined;
        const closeOnOutsideClick = event => {
            if (event.target.closest?.('[data-keep-settings-open]')) return; // the subscription dialog lives outside the panel
            if (panelRef.current && !panelRef.current.contains(event.target)) setOpen(false);
        };
        const closeOnEscape = event => {
            if (event.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', closeOnOutsideClick);
        document.addEventListener('keydown', closeOnEscape);
        return () => {
            document.removeEventListener('mousedown', closeOnOutsideClick);
            document.removeEventListener('keydown', closeOnEscape);
        };
    }, [open]);

    return (
        <div className="admin-theme-settings" ref={panelRef}>
            {open && (
                <section className="admin-theme-panel" aria-label="Theme settings">
                    <div className="admin-theme-panel__header">
                        <div>
                            <div className="admin-theme-panel__eyebrow"><Palette size={14} /> Appearance</div>
                            <h2>Settings</h2>
                            <p>Theme, colours and site-wide switches.</p>
                        </div>
                        <button className="admin-theme-close" type="button" onClick={() => setOpen(false)} aria-label="Close theme settings">
                            <X size={18} />
                        </button>
                    </div>

                    <div className="admin-theme-section">
                        <span className="admin-theme-label">Theme</span>
                        <div className="admin-theme-mode-grid">
                            {MODES.map(({ id, label, Icon }) => (
                                <button key={id} type="button" className={`admin-theme-mode ${mode === id ? 'is-active' : ''}`} onClick={() => setMode(id)}>
                                    <span className="admin-theme-mode__preview"><Icon size={18} /></span>
                                    <span>{label}</span>
                                    {mode === id && <Check className="admin-theme-mode__check" size={13} />}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="admin-theme-section">
                        <span className="admin-theme-label">Primary color</span>
                        <div className="admin-theme-colors">
                            {COLORS.map(option => (
                                <button
                                    key={option.id}
                                    type="button"
                                    className={`admin-theme-swatch ${color === option.id ? 'is-active' : ''}`}
                                    style={{ '--swatch-color': option.value }}
                                    onClick={() => setColor(option.id)}
                                    aria-label={`${option.label} primary color`}
                                    title={option.label}
                                >
                                    {color === option.id && <Check size={15} />}
                                </button>
                            ))}
                        </div>
                    </div>

                    {withSubscription && <SubscriptionExpirySetting />}
                </section>
            )}

            <button
                type="button"
                className={`admin-theme-trigger ${open ? 'is-open' : ''}`}
                onClick={() => setOpen(value => !value)}
                aria-label="Open theme settings"
                aria-expanded={open}
                title="Theme settings"
            >
                <Settings size={21} />
            </button>
        </div>
    );
};

export default ThemeSettings;
